import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { restoreSecureSession } from '@/services/session-bootstrap';
import { SECURE_SESSION } from '@/services/api';
import { isTutorialMode } from '@/tutorial/mode';

interface SessionGateProps { readonly children: ReactNode }
let restoration: Promise<void> | undefined;

/** Protected components never mount with a persisted identity and no live JWT. */
export function SessionGate({ children }: SessionGateProps) {
  const { t } = useTranslation('auth');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(
    !SECURE_SESSION || isTutorialMode() ? 'ready' : 'loading',
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!SECURE_SESSION || isTutorialMode()) return;
    let active = true;
    restoration ??= restoreSecureSession();
    void restoration.then(() => { if (active) setState('ready'); }, () => {
      restoration = undefined;
      if (active) setState('error');
    });
    return () => { active = false; };
  }, [attempt]);
  if (state === 'ready') return <>{children}</>;
  return <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6" aria-live="polite">
    <p role={state === 'error' ? 'alert' : 'status'}>{t(state === 'error' ? 'session.restoreError' : 'session.restoring')}</p>
    {state === 'error' && <Button onClick={() => { setState('loading'); setAttempt((value) => value + 1); }}>{t('session.retry')}</Button>}
  </main>;
}
